import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useCreateVitals } from '../../../hooks/history/useCreateVitals';
import { useUpdateVitals } from '../../../hooks/history/useUpdateVitals';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { isValidCurrency } from '../../../utils/isValidCurrency';

export default function VitalsForm({ successCallback = null, history = null, historyEntry = null, pet = null, vitals = null, mode = "create", open, onClose }) {

    const { t } = useTranslation('common'); 

    const [temp, setTemp] = React.useState("");
    const [pulse, setPulse] = React.useState("");
    const [respiration, setRespiration] = React.useState("");
    const [weight, setWeight] = React.useState("");
    const [mmc, setMmc] = React.useState("");
    const [crt, setCrt] = React.useState("");
    const [pain, setPain] = React.useState("");
    const [mentation, setMentation] = React.useState("");

    const { create } = useCreateVitals(successCallback);
    const { update } = useUpdateVitals(successCallback);

    const { showSnackbar } = useSnackbar();

    React.useEffect(() => {
        // reset field when there are changes (e.g. when opening edit)
        if (open)
        {
          setTemp(vitals?.temp || "");
          setPulse(vitals?.pulse || "");
          setRespiration(vitals?.respiration || "");
          setWeight(vitals?.weight || "");
          setMmc(vitals?.mmc || "");
          setCrt(vitals?.crt || "");
          setPain(vitals?.pain || "");
          setMentation(vitals?.mentation || "");
        }
    }, [vitals, open]);

    const handleSave = async () => {
        if (!weight.trim()) {
            showSnackbar(t('vitals.messages.weight_required'), "warning");
            return;
        }

        if (temp)
        {
          if (!isValidCurrency(temp)) {
            showSnackbar(t('vitals.messages.temp_invalid'), "warning");
            return;
          }
        }       
        if (pulse)
        {
          if (!isValidCurrency(pulse)) {
            showSnackbar(t('vitals.messages.pulse_invalid'), "warning");
            return;
          }
        }
        if (respiration)
        {
          if (!isValidCurrency(respiration)) {
            showSnackbar(t('vitals.messages.respiration_invalid'), "warning");
            return;
          }
        }   
        if (weight && !isValidCurrency(weight)) {
            showSnackbar(t('vitals.messages.weight_invalid'), "warning");
            return;
        }
        if (crt && !isValidCurrency(crt)) {
            showSnackbar(t('vitals.messages.crt_invalid'), "warning");
            return;
        }
        if (pain && !isValidCurrency(pain)) {
            showSnackbar(t('vitals.messages.pain_invalid'), "warning");
            return;
        }

        try 
        {
            if (mode === "create") {
                await create({ 
                  pet_id: pet.id,
                  history_id: history.id,
                  history_entry_id: historyEntry ? historyEntry.id : null,
                  temp: Number(temp) || null,
                  pulse: Number(pulse) || null,
                  respiration: Number(respiration) || null,
                  weight: Number(weight) || null,
                  mmc: mmc || null,
                  crt: Number(crt) || null,
                  pain: Number (pain) || null,
                  mentation: mentation || null
                });
                showSnackbar(t('vitals.messages.create_success'), "success");
            } else if (mode === "edit" && vitals) {
                await update(vitals.id, { 
                  temp: Number(temp) || null,
                  pulse: Number(pulse) || null,
                  respiration: Number(respiration),
                  weight: Number(weight) || null,
                  mmc: mmc || null,
                  crt: Number(crt) || null,
                  pain: Number (pain) || null,
                  mentation: mentation || null
                });
                showSnackbar(t('vitals.messages.update_success'), "success");
            }
            // clear field after save
            setTemp("");
            setPulse("");
            setRespiration("");
            setWeight("");
            setMmc("");
            setCrt("");
            setPain("");
            setMentation("");
            onClose();     // close dialog
        } catch (err) {
            showSnackbar(t('vitals.messages.save_fail'), "error");
        }

    };

    const handleCancel = () => {
      setTemp("");
      setPulse("");
      setRespiration("");
      setWeight("");
      setMmc("");
      setCrt("");
      setPain("");
      setMentation("");
      onClose();
    };

    return (
        <>
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>

        <DialogTitle>{mode === "create" ? "Add Vitals" : "Edit Vitals"}</DialogTitle>

        <DialogContent dividers>
            <Stack spacing={1}>
              <Typography variant="h6">
                Core Vitals
              </Typography>
              <TextField
                type='number'
                label="Body Weight (kg)"
                variant="outlined"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                fullWidth
              />
              <TextField
                type='number'
                label="Temperature (&deg;C)"
                variant="outlined"
                value={temp}
                onChange={(e) => setTemp(e.target.value)}
                fullWidth
              />
              <TextField
                type='number'
                label="Pulse Rate"
                variant="outlined"
                value={pulse}
                onChange={(e) => setPulse(e.target.value)}
                fullWidth
              />
              <TextField
                type='number'
                label="Respiration Rate"
                variant="outlined"
                value={respiration}
                onChange={(e) => setRespiration(e.target.value)}
                fullWidth
              />
              <br />
              <Typography variant="h6">
                Optional Vitals
              </Typography>
              <TextField
                  label="Mucous Membrane Color"
                  variant="outlined"
                  value={mmc}
                  onChange={(e) => setMmc(e.target.value)}
                  fullWidth
              />
              <TextField
                type='number'
                label="Capillary Refill Time (s)"
                variant="outlined"
                value={crt}
                onChange={(e) => setCrt(e.target.value)}
                fullWidth
              />
              <TextField
                type='number'
                label="Pain Score"
                variant="outlined"
                value={pain}
                onChange={(e) => setPain(e.target.value)}
                fullWidth
              />
              <TextField
                label="Mentation"
                variant="outlined"
                value={mentation}
                onChange={(e) => setMentation(e.target.value)}
                fullWidth
              />
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button onClick={handleSave}>
                Save
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
        </Dialog>
        </>
    );
}